import { T, useGT } from 'gt-react';
import { LockSimpleIcon } from '@phosphor-icons/react';
import { formatDateTime } from '@erp/shared-utils';
import type { GetSuggestionDetailResponse } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { NO_FACT, type LineFacts } from '../utils/lineFacts';
import {
  Fact,
  FactGroup,
  ForecastMinMaxFacts,
  PolicyFloorFacts,
  Quantity,
} from './LineFactsPanel';

export type LinePlanningDetail = Pick<
  GetSuggestionDetailResponse,
  | 'safetyStock'
  | 'targetAvailability'
  | 'forecastCalculatedAt'
  | 'leadTimeOverrideDays'
>;

/** A probability as a percentage, keeping the setting's precision (99.5%). */
function percent(value: number | null | undefined): string {
  if (value == null) return NO_FACT;
  return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

/**
 * Every planning input behind the line's quantity, for reading only
 * (GEN-3285): planning is the planner's decision, so the footer has no
 * inputs. Changes go through Forecast Detail, where planner permissions
 * apply.
 */
export function LinePlanningTab({
  policy,
  detail,
}: {
  policy: LineFacts['policy'];
  detail: LinePlanningDetail | undefined;
}) {
  const gt = useGT();
  return (
    <div className="space-y-3 p-3 text-xs">
      <p className="flex items-center gap-1 text-muted-foreground">
        <LockSimpleIcon aria-hidden className="h-3 w-3" />
        <T context="buying board line planning tab: buyers can only read these values">
          Read-only. Planners set these in Forecast Detail.
        </T>
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-3">
        <FactGroup
          title={<T context="line planning group: forecast levels">Forecast</T>}
        >
          <ForecastMinMaxFacts policy={policy} />
          <Fact
            label={
              <T context="line planning: forecast safety stock">Safety stock</T>
            }
          >
            <Quantity value={detail?.safetyStock ?? null} />
          </Fact>
          <Fact
            label={
              <T context="line planning: target service level, a percentage">
                Target availability
              </T>
            }
          >
            {percent(detail?.targetAvailability)}
          </Fact>
        </FactGroup>
        <FactGroup
          title={<T context="line planning group: stocking policy">Policy</T>}
        >
          <PolicyFloorFacts policy={policy} />
        </FactGroup>
        <FactGroup
          title={<T context="line planning group: timing inputs">Timing</T>}
        >
          <Fact
            label={
              <T context="line planning: a pinned lead time, in days">
                Lead time override
              </T>
            }
          >
            {detail?.leadTimeOverrideDays == null
              ? NO_FACT
              : gt('{days} days', {
                  context: 'line planning: lead time override in days',
                  days: Math.round(
                    detail.leadTimeOverrideDays,
                  ).toLocaleString(),
                })}
          </Fact>
          <Fact
            label={
              <T context="line planning: when the forecast was last computed">
                Forecast calculated
              </T>
            }
          >
            {detail?.forecastCalculatedAt == null
              ? NO_FACT
              : formatDateTime(detail.forecastCalculatedAt)}
          </Fact>
        </FactGroup>
      </div>
    </div>
  );
}
